// Diálogo único de las acciones MDM: normal (con payload) y sin backend (solo explica y se cierra).
// Avisa de lo que hay que saber ANTES de apretar (fraude, autolock, PIN, catálogo provisional); si
// la acción APLICA lo decide `actionEligibility.ts`, no este archivo.
import { useId, useState, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { LoanDeviceLockReasonEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceLockReasonEnum';
import { DeviceNotifyIntensityEnum } from '@fiado/type-kit/bin/mdm/enums/DeviceNotifyIntensityEnum';
import { DeviceReleaseReasonEnum } from '@fiado/type-kit/bin/mdm/enums/DeviceReleaseReasonEnum';
import { MdmProviderEnum } from '@fiado/type-kit/bin/mdm/enums/MdmProviderEnum';
import { MdmLockModeEnum } from '@fiado/type-kit/bin/retailCatalog/enums/MdmLockModeEnum';
import type {
    LoanDeviceNotifyRequest,
    LoanDeviceReleaseRequest,
} from '@/api/services/loan-credit/contract';
import {
    MDM_PROVIDER_LABEL,
    MDM_RELEASE_REASON_LABEL,
    MDM_RELEASE_REASONS_ALPHABETICAL,
} from '../logic';
import {
    DEFAULT_NOTIFICATION_INTENSITY,
    isMdmNoticeType,
    MDM_NOTICE_OPTIONS,
    MDM_NOTIFY_INTENSITY_LABEL,
    noticeTextOf,
} from '../notificationCatalog';
import type { MdmNoticeType } from '../../customerMessages/types';
import { describeAction, type MdmAction, type MdmActionKey } from '../mdmActions';
import type { MdmFleetDevice } from '../types';

/** Lo que el operador teclea antes de ejecutar; cada acción pide solo sus campos. */
export interface MdmActionPayload {
    /** El challenge que el cliente lee en su equipo bloqueado. Solo en PIN-UNLOCK. */
    devicePasskey?: string;
    /** Nuevo corte del equipo, ya en ISO-8601. Solo lo pide extender vigencia. */
    newDueDate?: string;
    /** Motivo del operador, ya recortado. Solo lo pide extender vigencia. */
    comment?: string;
    /** El aviso en pantalla ya armado. Solo lo pide notificar. */
    notification?: LoanDeviceNotifyRequest;
    /** El motivo y la nota de la liberación. Solo los pide liberar del MDM. */
    release?: LoanDeviceReleaseRequest;
}

/**
 * Un `datetime-local` («2026-09-30T20:00») al instante ISO que espera el lambda. El navegador lo
 * entrega en la zona del operador, así que `new Date` lo interpreta ahí y `toISOString` lo fija.
 */
export function toIsoInstant(localValue: string): string | null {
    const parsed = new Date(localValue);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

/** `true` cuando son solo dígitos y exactamente los que declara el proveedor del equipo. */
export function isDevicePasskey(value: string, length: number): boolean {
    return value.length === length && /^\d+$/.test(value);
}

/**
 * Espeja la validación del lambda: `IsFutureInstant` exige `newDueDate > now`. Validarlo aquí evita
 * que el rechazo llegue DESPUÉS del desbloqueo, con el equipo ya libre y sin extensión.
 */
export function isFutureInstant(localValue: string, now: Date | number = Date.now()): boolean {
    const iso = toIsoInstant(localValue);
    return iso !== null && new Date(iso).getTime() > new Date(now).getTime();
}

/** Motivo de extender vigencia: el lambda admite de 1 a 200 caracteres. */
export const EXTEND_COMMENT_MAX_LENGTH = 200;

/** Nota del operador que viaja con la liberación; el lambda la corta en 500. */
export const RELEASE_COMMENT_MAX_LENGTH = 500;

/** Lo que el operador elige del aviso; la cadena vacía es «todavía no elige». */
export interface MdmNotificationDraft {
    noticeType: MdmNoticeType | '';
    intensity: DeviceNotifyIntensityEnum;
}

/** El diálogo abre sin aviso: su texto afirma cosas del equipo y no se preselecciona. */
export function emptyNotificationDraft(): MdmNotificationDraft {
    return { noticeType: '', intensity: DEFAULT_NOTIFICATION_INTENSITY };
}

/** El body del notify, o `null` mientras falte el aviso o la intensidad no sea del enum. */
export function toNotifyRequest(draft: MdmNotificationDraft): LoanDeviceNotifyRequest | null {
    if (draft.noticeType === '') return null;
    if (!Object.values(DeviceNotifyIntensityEnum).includes(draft.intensity)) return null;
    return { noticeType: draft.noticeType, intensity: draft.intensity };
}

/**
 * El candado de lo irreversible: el operador teclea el IMEI COMPLETO del equipo. Solo se ignoran
 * los espacios de los extremos, que sobran al pegar; cualquier otra diferencia no confirma nada.
 */
export function isImeiConfirmed(typed: string, imei: string): boolean {
    return typed.trim() === imei && imei !== '';
}

/**
 * Cuándo se confirma la liberación en cada proveedor. Todos la ejecutan; lo que cambia es si el
 * expediente queda liberado en el acto o después.
 */
export const MDM_RELEASE_TIMING: Record<MdmProviderEnum, string> = {
    [MdmProviderEnum.TRUSTONIC]:
        'En Trustonic la liberación es inmediata: el proveedor la confirma en el acto y el expediente queda liberado.',
    [MdmProviderEnum.DATACULTR]:
        'En Datacultr la liberación es asíncrona: la orden queda pendiente y el proveedor la confirma después. El expediente no cambia hasta entonces.',
    [MdmProviderEnum.MOTOSAFE]:
        'En Motosafe la liberación es asíncrona: la orden queda pendiente hasta que el proveedor reporta el equipo liberado. El expediente no cambia hasta entonces.',
};

/** De dónde sale el código que pide el formulario del PIN. */
const PASSKEY_HINT =
    'Pídeselo al cliente en el momento: es el código que muestra la pantalla de su equipo bloqueado.';

const passkeyInvalidHint = (length: number): string =>
    `Son ${String(length)} dígitos exactos: sin ellos el formulario no envía la solicitud.`;

const passkeyLabel = (length: number): string =>
    `Código de ${String(length)} dígitos que muestra la pantalla del equipo`;

/** Sin largo declarado no hay forma honesta de validar el código: no se pide ni se envía. */
export const PASSKEY_LENGTH_UNKNOWN = 'El proveedor no declaró el largo del código.';

/** El PIN abre el equipo un rato; el candado sigue vigente del lado del proveedor. */
const PIN_IS_TEMPORARY =
    'El PIN NO levanta el candado: abre el equipo un rato y se vuelve a bloquear solo. El desbloqueo de verdad es «Desbloquear».';

export interface MdmActionDialogProps {
    /** `null` cierra el diálogo: la acción abierta es el estado. */
    action: MdmAction | null;
    /** El equipo sobre el que se ejecuta la acción; `null` también cierra el diálogo. */
    device: MdmFleetDevice | null;
    /** La acción confirmada sigue en vuelo: el botón se apaga y lo dice. */
    isPending?: boolean;
    onCancel: () => void;
    onConfirm: (payload: MdmActionPayload) => void;
}

/** La orden como sustantivo, para «Se enviará la orden de bloqueo al proveedor…». */
const ACTION_ORDER_NOUN: Record<MdmActionKey, string> = {
    lock: 'bloqueo',
    temporaryLock: 'bloqueo temporal',
    unlock: 'desbloqueo',
    refresh: 'actualización desde el proveedor',
    notify: 'notificación',
    extendValidity: 'extensión de vigencia',
    pinUnlock: 'desbloqueo con PIN',
    release: 'liberación',
};

const DIALOG_TITLE: Record<MdmActionKey, string> = {
    lock: 'Bloquear equipo',
    temporaryLock: 'Bloqueo temporal del equipo',
    unlock: 'Desbloquear equipo (sigue en el MDM)',
    refresh: 'Actualizar desde el proveedor',
    notify: 'Notificar en la pantalla del equipo',
    extendValidity: 'Extender vigencia del equipo',
    pinUnlock: 'PIN-UNLOCK · desbloqueo sin conexión',
    release: 'Liberar del MDM — sacar el equipo para siempre',
};

const FIELD_LABEL = 'text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground';

/** Franja ámbar de aviso antes de confirmar una acción. */
function WarningStrip({ children }: { children: ReactNode }) {
    return (
        <div className="flex gap-2 rounded-md border border-warning-soft-border bg-warning-soft px-3 py-2 text-[11.5px] leading-relaxed text-warning">
            <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
            <span>{children}</span>
        </div>
    );
}

/** Datacultr resuelve el aviso contra su catálogo, que todavía es provisional; Trustonic no. */
function ProvisionalNotificationStrip({ device }: { device: MdmFleetDevice }) {
    if (device.mdmProvider !== MdmProviderEnum.DATACULTR) return null;
    return (
        <WarningStrip>
            <strong className="font-bold">
                {`En ${MDM_PROVIDER_LABEL[MdmProviderEnum.DATACULTR]} el catálogo de avisos todavía es provisional.`}
            </strong>{' '}
            Puede que el proveedor no reconozca el aviso y no llegue a la pantalla del equipo.
            Puedes enviarlo, pero no lo des por entregado.
        </WarningStrip>
    );
}

/** Un aviso de regla de negocio que se pinta en franja antes de confirmar. */
export interface MdmUnlockWarning {
    headline: string;
    body: string;
}

/**
 * En modo programado un UNLOCK manual no cancela el autolock: el equipo se vuelve a bloquear solo.
 * Devuelve `null` cuando la acción no es UNLOCK o el equipo no está en ese modo.
 */
export function readScheduledUnlockWarning(
    action: MdmAction,
    device: MdmFleetDevice,
): MdmUnlockWarning | null {
    if (action.key !== 'unlock') return null;
    if (device.mdmLockMode !== MdmLockModeEnum.SCHEDULED) return null;
    return {
        headline: 'El autolock programado sigue vigente.',
        body: 'El equipo se vuelve a bloquear solo. Para liberarlo de verdad hay que reprogramarlo con fecha explícita.',
    };
}

/**
 * Un bloqueo por fraude no se levanta con la regla de pago: hace falta autorización. Devuelve
 * `null` cuando la acción no es UNLOCK o el equipo no está bloqueado por fraude.
 */
export function readFraudUnlockWarning(
    action: MdmAction,
    device: MdmFleetDevice,
): MdmUnlockWarning | null {
    if (action.key !== 'unlock') return null;
    if (device.lockReason !== LoanDeviceLockReasonEnum.FRAUD) return null;
    return {
        headline: 'Este equipo está bloqueado por fraude.',
        body: 'No lo desbloquees sin autorización: la regla de pago no aplica aquí.',
    };
}

/** El aviso de que desbloquear por fraude necesita autorización, en el punto de no retorno. */
function FraudUnlockStrip({ action, device }: { action: MdmAction; device: MdmFleetDevice }) {
    const warning = readFraudUnlockWarning(action, device);
    if (!warning) return null;
    return (
        <WarningStrip>
            <strong className="font-bold">{warning.headline}</strong> {warning.body}
        </WarningStrip>
    );
}

/** El aviso de que el desbloqueo no libera al equipo mientras el autolock siga programado. */
function ScheduledUnlockStrip({ action, device }: { action: MdmAction; device: MdmFleetDevice }) {
    const warning = readScheduledUnlockWarning(action, device);
    if (!warning) return null;
    return (
        <WarningStrip>
            <strong className="font-bold">{warning.headline}</strong> {warning.body}
        </WarningStrip>
    );
}

/** Estilo compartido de los combos del diálogo; el mismo de los filtros de la pantalla. */
const SELECT_FIELD =
    'h-7 w-full rounded-[5px] border border-border bg-card px-1.5 text-[11.5px] text-foreground';

const FIELD_HINT = 'text-[10.5px] leading-snug text-muted-foreground';

/** El aviso del catálogo, su intensidad y, en solo lectura, el texto que lee el cliente. */
export function NotifyFields({
    fieldId,
    draft,
    onChange,
    showPreview = true,
}: {
    fieldId: string;
    draft: MdmNotificationDraft;
    onChange: (next: MdmNotificationDraft) => void;
    /** Solo con un equipo Trustonic: el texto de la vista previa es el suyo. */
    showPreview?: boolean;
}) {
    const text = !showPreview || draft.noticeType === '' ? null : noticeTextOf(draft.noticeType);
    return (
        <div className="space-y-3">
            <div className="space-y-1">
                <label className={FIELD_LABEL} htmlFor={`${fieldId}-notice`}>
                    Aviso
                </label>
                <select
                    id={`${fieldId}-notice`}
                    className={SELECT_FIELD}
                    value={draft.noticeType}
                    onChange={(event) => {
                        const { value } = event.target;
                        onChange({ ...draft, noticeType: isMdmNoticeType(value) ? value : '' });
                    }}
                >
                    <option value="">Elige el aviso</option>
                    {MDM_NOTICE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <p className={FIELD_HINT}>
                    El texto lo redacta el conector de MDM y no se edita desde aquí.
                </p>
            </div>

            <fieldset className="space-y-1">
                <legend className={FIELD_LABEL}>Cómo se ve en el equipo</legend>
                {Object.values(DeviceNotifyIntensityEnum).map((intensity) => (
                    <label
                        key={intensity}
                        className="flex items-start gap-2 text-[11.5px] leading-snug text-foreground"
                    >
                        <input
                            type="radio"
                            className="mt-0.5 accent-primary"
                            name={`${fieldId}-intensity`}
                            value={intensity}
                            checked={draft.intensity === intensity}
                            onChange={() => {
                                onChange({ ...draft, intensity });
                            }}
                        />
                        <span>{MDM_NOTIFY_INTENSITY_LABEL[intensity]}</span>
                    </label>
                ))}
            </fieldset>

            {text === null ? null : (
                <div className="space-y-1">
                    <div className={FIELD_LABEL}>
                        {`Lo que lee el cliente en ${MDM_PROVIDER_LABEL[MdmProviderEnum.TRUSTONIC]}`}
                    </div>
                    <div className="rounded-md border border-border bg-muted/25 px-3 py-2">
                        <p className="text-[12px] font-semibold text-foreground">{text.title}</p>
                        <p className="mt-0.5 text-[11.5px] leading-snug text-muted-foreground">
                            {text.content}
                        </p>
                    </div>
                    <p className={FIELD_HINT}>
                        {`${MDM_PROVIDER_LABEL[MdmProviderEnum.DATACULTR]} muestra el texto de su propio catálogo.`}
                    </p>
                </div>
            )}
        </div>
    );
}

/** El motivo, la nota y el IMEI que confirma la liberación definitiva. */
function ReleaseFields({
    fieldId,
    device,
    reason,
    comment,
    imeiConfirmation,
    onReasonChange,
    onCommentChange,
    onImeiChange,
}: {
    fieldId: string;
    device: MdmFleetDevice;
    reason: DeviceReleaseReasonEnum | '';
    comment: string;
    imeiConfirmation: string;
    onReasonChange: (next: DeviceReleaseReasonEnum | '') => void;
    onCommentChange: (next: string) => void;
    onImeiChange: (next: string) => void;
}) {
    const confirmed = isImeiConfirmed(imeiConfirmation, device.imei);
    return (
        <div className="space-y-3">
            <div className="space-y-1">
                <label className={FIELD_LABEL} htmlFor={`${fieldId}-release-reason`}>
                    Motivo de la liberación
                </label>
                <select
                    id={`${fieldId}-release-reason`}
                    className={SELECT_FIELD}
                    value={reason}
                    onChange={(event) => {
                        onReasonChange(event.target.value as DeviceReleaseReasonEnum | '');
                    }}
                >
                    <option value="">Elige un motivo</option>
                    {MDM_RELEASE_REASONS_ALPHABETICAL.map((value) => (
                        <option key={value} value={value}>
                            {MDM_RELEASE_REASON_LABEL[value]}
                        </option>
                    ))}
                </select>
            </div>

            <div className="space-y-1">
                <label className={FIELD_LABEL} htmlFor={`${fieldId}-release-comment`}>
                    Nota (opcional)
                </label>
                <Textarea
                    id={`${fieldId}-release-comment`}
                    className="min-h-[60px] text-[12px]"
                    value={comment}
                    maxLength={RELEASE_COMMENT_MAX_LENGTH}
                    aria-describedby={`${fieldId}-release-comment-hint`}
                    onChange={(event) => {
                        onCommentChange(event.target.value);
                    }}
                />
                <p id={`${fieldId}-release-comment-hint`} className={FIELD_HINT}>
                    {`${String(comment.length)} de ${String(RELEASE_COMMENT_MAX_LENGTH)} caracteres · queda en el expediente del equipo`}
                </p>
            </div>

            <div className="space-y-1">
                <label className={FIELD_LABEL} htmlFor={`${fieldId}-release-imei`}>
                    Escribe el IMEI completo para confirmar
                </label>
                <Input
                    id={`${fieldId}-release-imei`}
                    inputMode="numeric"
                    autoComplete="off"
                    className="font-mono"
                    placeholder={device.imei}
                    value={imeiConfirmation}
                    aria-describedby={`${fieldId}-release-imei-hint`}
                    onChange={(event) => {
                        onImeiChange(event.target.value);
                    }}
                />
                <p id={`${fieldId}-release-imei-hint`} className={FIELD_HINT}>
                    {confirmed
                        ? 'El IMEI coincide.'
                        : `Teclea ${device.imei} tal cual. Es lo único que habilita una acción que no se puede deshacer.`}
                </p>
            </div>
        </div>
    );
}

function ActionDialogBody({
    action,
    device,
    isPending = false,
    onCancel,
    onConfirm,
}: MdmActionDialogProps & { action: MdmAction; device: MdmFleetDevice }) {
    const fieldId = useId();
    const [dueDateLocal, setDueDateLocal] = useState('');
    const [passkey, setPasskey] = useState('');
    const [extendComment, setExtendComment] = useState('');
    const [notification, setNotification] = useState(emptyNotificationDraft);
    const [releaseReason, setReleaseReason] = useState<DeviceReleaseReasonEnum | ''>('');
    const [releaseComment, setReleaseComment] = useState('');
    const [imeiConfirmation, setImeiConfirmation] = useState('');

    const needsDueDate = action.payloadFields.includes('newDueDate');
    const dueDateValid = isFutureInstant(dueDateLocal);
    const needsPasskey = action.payloadFields.includes('devicePasskey');
    const passkeyLength = device.devicePasskeyLength;
    const passkeyValid = passkeyLength !== undefined && isDevicePasskey(passkey, passkeyLength);
    const trimmedExtendComment = extendComment.trim();
    const needsNotification = action.payloadFields.includes('notification');
    const notifyRequest = toNotifyRequest(notification);
    const needsRelease = action.payloadFields.includes('release');
    // Lo irreversible se habilita con las dos cosas: un motivo elegido y el IMEI tecleado completo.
    const releaseValid = releaseReason !== '' && isImeiConfirmed(imeiConfirmation, device.imei);

    const description = describeAction(action, device.mdmProvider);
    const title = DIALOG_TITLE[action.key];
    // Sin este estado el operador vuelve a apretar mientras la orden sigue en vuelo.
    const confirmLabel = isPending ? 'Ejecutando…' : 'Ejecutar';

    return (
        <DialogContent className="max-w-lg">
            <DialogHeader>
                <DialogTitle className="text-base">{title}</DialogTitle>
                <DialogDescription className="text-[11.5px]">
                    {`IMEI ${device.imei} · ${MDM_PROVIDER_LABEL[device.mdmProvider]}`}
                </DialogDescription>
            </DialogHeader>

            <div className="min-w-0 space-y-3">
                <FraudUnlockStrip action={action} device={device} />
                <ScheduledUnlockStrip action={action} device={device} />
                {!needsPasskey ? null : (
                    <WarningStrip>
                        <strong className="font-bold">Esto no levanta el candado.</strong>{' '}
                        {PIN_IS_TEMPORARY}
                    </WarningStrip>
                )}
                {!needsNotification ? null : <ProvisionalNotificationStrip device={device} />}
                {!needsRelease ? null : (
                    <WarningStrip>
                        <strong className="font-bold">
                            Esto es irreversible: saca el equipo del MDM para siempre.
                        </strong>{' '}
                        No es un desbloqueo. El equipo deja de estar administrado y nunca más se
                        podrá bloquear, desbloquear ni consultar desde aquí.{' '}
                        {MDM_RELEASE_TIMING[device.mdmProvider]}
                    </WarningStrip>
                )}
                {needsDueDate ? (
                    <WarningStrip>
                        <strong className="font-bold">Esto mueve una fecha de cobro.</strong> Son
                        dos órdenes al proveedor: primero desbloquear, después reprogramar el corte.
                        Si la segunda falla, el equipo queda libre y sin bloqueo automático.
                    </WarningStrip>
                ) : null}

                <p className="text-[12px] leading-relaxed text-foreground">
                    Se enviará la orden de{' '}
                    <strong className="font-semibold">{ACTION_ORDER_NOUN[action.key]}</strong>{' '}
                    {`al proveedor ${MDM_PROVIDER_LABEL[device.mdmProvider]}. `}
                    {/* La descripción va aquí para que el operador la lea en el punto de no
                            retorno: en la ficha vive en el `?`. */}
                    <span className="text-muted-foreground">{description}</span>
                </p>

                {!needsDueDate ? null : (
                    <div className="space-y-1">
                        <label className={FIELD_LABEL} htmlFor={`${fieldId}-duedate`}>
                            Nueva fecha de corte
                        </label>
                        <Input
                            id={`${fieldId}-duedate`}
                            type="datetime-local"
                            value={dueDateLocal}
                            aria-describedby={`${fieldId}-duedate-hint`}
                            onChange={(event) => {
                                setDueDateLocal(event.target.value);
                            }}
                        />
                        <p
                            id={`${fieldId}-duedate-hint`}
                            className="text-[10.5px] leading-snug text-muted-foreground"
                        >
                            {dueDateLocal === '' || dueDateValid
                                ? 'Hasta esta fecha y hora el equipo queda libre; después se bloquea solo.'
                                : 'La fecha tiene que ser futura: el proveedor rechaza un corte que ya pasó.'}
                        </p>
                    </div>
                )}

                {!needsDueDate ? null : (
                    <div className="space-y-1">
                        <label className={FIELD_LABEL} htmlFor={`${fieldId}-extend-comment`}>
                            Motivo
                        </label>
                        <Textarea
                            id={`${fieldId}-extend-comment`}
                            className="min-h-[60px] text-[12px]"
                            value={extendComment}
                            maxLength={EXTEND_COMMENT_MAX_LENGTH}
                            aria-describedby={`${fieldId}-extend-comment-hint`}
                            onChange={(event) => {
                                setExtendComment(event.target.value);
                            }}
                        />
                        <p id={`${fieldId}-extend-comment-hint`} className={FIELD_HINT}>
                            {`${String(extendComment.length)} de ${String(EXTEND_COMMENT_MAX_LENGTH)} caracteres · queda en la bitácora del equipo`}
                        </p>
                    </div>
                )}

                {!needsPasskey ? null : passkeyLength === undefined ? (
                    <WarningStrip>{PASSKEY_LENGTH_UNKNOWN}</WarningStrip>
                ) : (
                    <div className="space-y-1">
                        <label className={FIELD_LABEL} htmlFor={`${fieldId}-passkey`}>
                            {passkeyLabel(passkeyLength)}
                        </label>
                        <Input
                            id={`${fieldId}-passkey`}
                            inputMode="numeric"
                            autoComplete="off"
                            maxLength={passkeyLength}
                            placeholder={'1'.repeat(passkeyLength)}
                            value={passkey}
                            aria-describedby={`${fieldId}-passkey-hint`}
                            onChange={(event) => {
                                setPasskey(event.target.value.replace(/\D/g, ''));
                            }}
                        />
                        <p
                            id={`${fieldId}-passkey-hint`}
                            className="text-[10.5px] leading-snug text-muted-foreground"
                        >
                            {passkey === '' || passkeyValid
                                ? PASSKEY_HINT
                                : passkeyInvalidHint(passkeyLength)}
                        </p>
                    </div>
                )}

                {!needsNotification ? null : (
                    <NotifyFields
                        fieldId={fieldId}
                        draft={notification}
                        onChange={setNotification}
                        showPreview={device.mdmProvider === MdmProviderEnum.TRUSTONIC}
                    />
                )}

                {!needsRelease ? null : (
                    <ReleaseFields
                        fieldId={fieldId}
                        device={device}
                        reason={releaseReason}
                        comment={releaseComment}
                        imeiConfirmation={imeiConfirmation}
                        onReasonChange={setReleaseReason}
                        onCommentChange={setReleaseComment}
                        onImeiChange={setImeiConfirmation}
                    />
                )}
            </div>

            <DialogFooter className="gap-2">
                <Button variant="outline" onClick={onCancel}>
                    Cancelar
                </Button>
                <Button
                    disabled={
                        isPending ||
                        (needsDueDate && (!dueDateValid || trimmedExtendComment === '')) ||
                        (needsPasskey && !passkeyValid) ||
                        (needsNotification && notifyRequest === null) ||
                        (needsRelease && !releaseValid)
                    }
                    onClick={() => {
                        onConfirm({
                            ...(needsDueDate
                                ? {
                                      newDueDate: toIsoInstant(dueDateLocal) ?? undefined,
                                      comment: trimmedExtendComment,
                                  }
                                : {}),
                            ...(needsPasskey ? { devicePasskey: passkey } : {}),
                            ...(needsNotification && notifyRequest
                                ? { notification: notifyRequest }
                                : {}),
                            ...(needsRelease && releaseReason !== ''
                                ? {
                                      release: {
                                          reason: releaseReason,
                                          ...(releaseComment.trim()
                                              ? { comment: releaseComment.trim() }
                                              : {}),
                                      },
                                  }
                                : {}),
                        });
                    }}
                >
                    {confirmLabel}
                </Button>
            </DialogFooter>
        </DialogContent>
    );
}

export function MdmActionDialog(props: MdmActionDialogProps) {
    const { action, device, onCancel } = props;
    // Sin acción o sin equipo no hay nada que confirmar: el diálogo ni se monta.
    const open = action !== null && device !== null;
    return (
        <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) onCancel();
            }}
        >
            {action && device ? (
                <ActionDialogBody {...props} action={action} device={device} key={action.key} />
            ) : null}
        </Dialog>
    );
}
